import { ActionTree } from 'vuex';
import {
  clearAllInformation,
  getInProgressData,
  getLandedCost,
  parseCarriersData,
  syncInProgressData,
} from '@/features/shipment/services/shipment.services';
import { showErrorAlertDialog } from '@/features/common/helpers';
import moment from 'moment';
import {
  AvailableService, GetRatesData, Shipment,
} from '../types';
import {
  setDefaultInprogressData,
} from '../helpers/createShipment';

const isFutureShipDate = (shipDateString: string | undefined): boolean => {
  if (!shipDateString) return false;

  const today = new Date();
  const shipDate = new Date(shipDateString);

  return shipDate >= today;
};

const hasMissingHsCodeInCustomsParts = (state: Shipment): boolean => {
  const customsParts = state.inProgress.commercialInvoice?.customsParts || [];
  return customsParts.some((part) => !part.hsCode?.trim());
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const actions: ActionTree<Shipment, any> = {
  async getRates({ commit, state }, payload): Promise<void> {
    const {
      date,
      showLoading,
      packages: packagesFromPayload,
      fromAddress: fromAddressFromPayload,
      toAddress: toAddressFromPayload,
      isReturnShipment: isReturnShipmentFromPayload,
      isBoxFree: isBoxFreeFromPayload,
      isLabelFree: isLabelFreeFromPayload,
    } = payload || {};

    const {
      fromAddress, toAddress, packages, documentsOnly, shipDate: inProgressShipDateTime,
      isReturnShipment, isBoxFree, isLabelFree,
    } = state.inProgress;

    let shipDateTime = moment().format('YYYY-MM-DD');

    if (inProgressShipDateTime && isFutureShipDate(inProgressShipDateTime)) {
      shipDateTime = moment(inProgressShipDateTime).format('YYYY-MM-DD');
    }

    if (date) {
      shipDateTime = moment(date).format('YYYY-MM-DD');
    }

    commit('patchInProgress', { key: 'shipDate', value: shipDateTime });

    const data: GetRatesData = {
      currencyCode: 'CAD',
      fromAddress: fromAddressFromPayload || fromAddress,
      toAddress: toAddressFromPayload || toAddress,
      packages: packagesFromPayload || packages,
      shipDateTime,
      documentsOnly,
      returnShipment: isReturnShipmentFromPayload || isReturnShipment,
      boxFree: isBoxFreeFromPayload || isBoxFree,
      labelFree: isLabelFreeFromPayload || isLabelFree,
    };

    if (showLoading !== false) {
      commit('setLoading', true);
    }

    commit('setReconnectAccountRequired', null);
    const ratesResponse = await parseCarriersData(data, 'CreateShipment');

    if (ratesResponse) {
      const { rates, reconnectAccounts } = ratesResponse;
      const services = rates.map((el: AvailableService, idx: number) => {
        const copy = el;
        copy.show = true;
        copy.id = idx + 1;
        return copy;
      });
      commit('setAvailableServices', services);
      commit('setReconnectAccountRequired', reconnectAccounts);
    } else {
      commit('setReconnectAccountRequired', null);
      showErrorAlertDialog();
    }

    if (showLoading !== false) {
      commit('setLoading', false);
    }
  },

  async syncInProgressData({ state }): Promise<void> {
    await syncInProgressData(state.inProgress);
  },

  setAndSyncInProgressData({ commit }, payload): void {
    commit('setInProgress', payload);
    syncInProgressData(payload);
  },

  saveInProgressData({ state, commit }, payload): void {
    commit('patchInProgress', payload);
    syncInProgressData(state.inProgress);
  },

  async fetchAndSaveInProgressData({ commit }): Promise<void> {
    const inProgressData = await getInProgressData();
    commit('setInProgress', inProgressData);
  },

  clearAllInformation({ commit }): void {
    setDefaultInprogressData();
    commit('incrementIsUserPreferences');
    clearAllInformation();
  },

  clearInProgressDataInShortly({ commit }) {
    setTimeout(() => {
      setDefaultInprogressData();
      commit('incrementIsUserPreferences');
    }, 500);
  },

  swapInProgressAddresses({ state, commit }, { fromAddress, toAddress }): void {
    const payload = {
      ...state.inProgress,
      fromAddress,
      toAddress,
    };

    commit('setInProgressSilently', payload);
    syncInProgressData(payload);
  },

  async fetchLandedCost(
    { state, commit },
    { service, isInternational }: { service: AvailableService, isInternational: boolean },
  ): Promise<void> {
    if (!isInternational) return;
    if (service.landedCost !== undefined) return;

    if (hasMissingHsCodeInCustomsParts(state)) {
      commit('setServiceLandedCost', { serviceId: service.id, landedCost: null });
      return;
    }

    const result = await getLandedCost(state.inProgress, service);
    commit('setServiceLandedCost', { serviceId: service.id, landedCost: result });
  },
};

export default actions;
